// @ts-nocheck
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { 
  DollarSign, 
  TrendingUp, 
  Users, 
  CreditCard, 
  AlertCircle,
  Eye,
  User,
  Hash,
  CheckCircle,
  XCircle,
  Clock,
  Download,
  FileText
} from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { format } from 'date-fns';

/**
 * Parse an event date that may be a date-only string ("2026-08-19") as LOCAL
 * midnight, so it renders the same calendar day everywhere (matching the Events
 * list). Plain `new Date("2026-08-19")` parses as UTC and shifts back a day in
 * timezones behind UTC.
 */
const parseEventDate = (value: string) =>
  /^\d{4}-\d{2}-\d{2}$/.test(value) ? new Date(value + 'T00:00:00') : new Date(value);
import { useAdminOrgScope } from '@/hooks/useAdminOrgScope';

interface DetailedLineItem {
  id: string;
  registration_id: string;
  item_type: string;
  item_name: string;
  unit_price_cents: number;
  quantity: number;
  line_total_cents: number;
  payment_status: string;
  team_name?: string;
  registration_date: string;
  qr_code_value: string;
  is_redeemed: boolean;
  redeemed_at?: string;
  redeemed_by?: string;
  // Registration details
  registrant_name: string;
  registrant_email: string;
  stripe_session_id?: string;
  registration_data?: any;
  created_at: string;
  updated_at: string;
}

interface EventFinanceData {
  event: {
    id: string;
    name: string;
    start_date: string;
    end_date: string;
  };
  revenue: {
    registration: number;
    spectator_tickets: number;
    extras: number;
    total: number;
  };
  counts: {
    total_registrations: number;
    paid_registrations: number;
    pending_registrations: number;
  };
  payment_breakdown: {
    paid: number;
    pending: number;
    comped: number;
    venmo_paid: number;
  };
  line_items: DetailedLineItem[];
}

const EventFinanceReports: React.FC = () => {
  const { eventId } = useParams<{ eventId: string }>();
  const navigate = useNavigate();
  const { scopeOrgId, ready } = useAdminOrgScope();
  const [financeData, setFinanceData] = useState<EventFinanceData | null>(null);
  const [loading, setLoading] = useState(true);
  const [paymentFilter, setPaymentFilter] = useState<string>('all');
  const [selectedLineItem, setSelectedLineItem] = useState<DetailedLineItem | null>(null);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [events, setEvents] = useState<Array<{id: string, name: string, start_date: string}>>([]);
  const [selectedEventId, setSelectedEventId] = useState<string>(eventId || '');

  useEffect(() => {
    if (!ready) return;
    fetchEvents();
  }, [ready, scopeOrgId]);

  useEffect(() => {
    if (selectedEventId) {
      fetchFinanceData(selectedEventId);
    } else if (eventId) {
      setSelectedEventId(eventId);
    } else {
      setLoading(false);
    }
  }, [eventId, selectedEventId]);

  const fetchEvents = async () => {
    try {
      // Org admin -> only their events; super admin -> all events.
      let query = supabase
        .from('events')
        .select('id, name, start_date')
        .order('start_date', { ascending: false });

      if (scopeOrgId) query = query.eq('organization_id', scopeOrgId);

      const { data, error } = await query;

      if (error) throw error;
      setEvents(data || []);
    } catch (error) {
      console.error('Error fetching events:', error);
    }
  };

  const fetchFinanceData = async (currentEventId: string) => {
    try {
      setLoading(true);

      // Fetch event details
      const { data: eventData, error: eventError } = await supabase
        .from('events')
        .select('id, name, start_date, end_date')
        .eq('id', currentEventId)
        .single();

      if (eventError) throw eventError;

      // Fetch registrations from new registrations table - include registered_by for purchaser info
      const { data: registrationsData, error: registrationsError } = await supabase
        .from('registrations')
        .select(`
          id,
          status,
          payment_status,
          created_at,
          updated_at,
          stripe_session_id,
          registrant_data,
          pricing_breakdown,
          registration_amount,
          extras_amount,
          special_request_amount,
          tickets_amount,
          registered_by
        `)
        .eq('event_id', currentEventId);

      if (registrationsError) throw registrationsError;

      // Fetch profile info for registered_by users to get purchaser names
      const registeredByIds = [...new Set((registrationsData || []).map(r => r.registered_by).filter(Boolean))];
      let purchaserProfiles: Record<string, { name: string; email: string }> = {};
      
      if (registeredByIds.length > 0) {
        const { data: profiles } = await supabase
          .from('profiles')
          .select('id, name, email')
          .in('id', registeredByIds);
        
        purchaserProfiles = (profiles || []).reduce((acc, p) => {
          acc[p.id] = { name: p.name || '', email: p.email || '' };
          return acc;
        }, {} as Record<string, { name: string; email: string }>);
      }
      
      // Transform to expected format - use registered_by purchaser info for extras attribution
      const transformedRegistrations = (registrationsData || []).map(reg => {
        const purchaser = reg.registered_by ? purchaserProfiles[reg.registered_by] : null;
        return {
          id: reg.id,
          // Use purchaser name/email if available, otherwise fall back to coach data
          name: purchaser?.name || reg.registrant_data?.coach?.name || reg.registrant_data?.contact_name || 'Unknown',
          email: purchaser?.email || reg.registrant_data?.coach?.email || reg.registrant_data?.contact_email || '',
          status: reg.status,
          payment_status: reg.payment_status,
          total_amount_cents: (reg.registration_amount || 0) * 100 + (reg.extras_amount || 0) * 100 + (reg.special_request_amount || 0) * 100 + (reg.tickets_amount || 0) * 100,
          created_at: reg.created_at,
          updated_at: reg.updated_at,
          stripe_session_id: reg.stripe_session_id,
          registration_data: reg.registrant_data,
          event_registration_line_items: [], // Line items now come from purchased_extras/purchased_tickets
          registered_by: reg.registered_by,
        };
      });

      // Fetch purchased extras from new modular system - include registered_by for purchaser info
      const { data: purchasedExtras } = await supabase
        .from('purchased_extras')
        .select(`
          id,
          extra_name,
          extra_price,
          qr_code,
          is_redeemed,
          redeemed_at,
          redeemed_by,
          created_at,
          registration_id,
          registrations (
            id,
            status,
            payment_status,
            registrant_data,
            registered_by
          )
        `)
        .eq('event_id', currentEventId);

      // Fetch purchased tickets from new modular system - include registered_by for purchaser info
      const { data: purchasedTickets } = await supabase
        .from('purchased_tickets')
        .select(`
          id,
          ticket_name,
          ticket_price,
          qr_code,
          is_redeemed,
          redeemed_at,
          redeemed_by,
          created_at,
          registration_id,
          registrations (
            id,
            status,
            payment_status,
            registrant_data,
            registered_by
          )
        `)
        .eq('event_id', currentEventId);

      // Process the data
      let totalRegistration = 0;
      let totalSpectatorTickets = 0;
      let totalExtras = 0;
      let paidAmount = 0;
      let pendingAmount = 0;
      let compedAmount = 0;
      let venmoPaidAmount = 0;
      
      const lineItems: DetailedLineItem[] = [];

      transformedRegistrations?.forEach(registration => {
        const registrationAmount = registration.total_amount_cents || 0;

        // Classify by ACTUAL payment status, which is the source of truth shown
        // per-row in the line-items table and on the Registrations dashboard
        // (Collected vs Outstanding). The lifecycle `status` (e.g. 'confirmed')
        // can be set even when payment is still outstanding, so only fall back to
        // it when payment_status is completely missing.
        const rawStatus = (registration.payment_status || registration.status || '')
          .toString()
          .toLowerCase();
        if (rawStatus === 'comped' || rawStatus === 'free' || rawStatus === 'waived') {
          compedAmount += registrationAmount;
        } else if (rawStatus === 'venmo_paid' || rawStatus === 'venmo') {
          venmoPaidAmount += registrationAmount;
        } else if (rawStatus === 'paid' || rawStatus === 'confirmed' || rawStatus === 'succeeded' || rawStatus === 'paid_in_full') {
          paidAmount += registrationAmount;
        } else {
          // pending, unpaid, processing, failed, null, etc. -> outstanding
          pendingAmount += registrationAmount;
        }


        // Process line items (now from purchased_extras/purchased_tickets separately)
        registration.event_registration_line_items?.forEach(lineItem => {
          const lineItemData: DetailedLineItem = {
            id: lineItem.id,
            registration_id: registration.id,
            item_type: lineItem.item_type,
            item_name: lineItem.item_name,
            unit_price_cents: lineItem.unit_price_cents,
            quantity: lineItem.quantity,
            line_total_cents: lineItem.line_total_cents,
            payment_status: registration.status,
            team_name: registration.name,
            registration_date: registration.created_at,
            qr_code_value: lineItem.qr_code_value || '',
            is_redeemed: lineItem.is_redeemed || false,
            redeemed_at: lineItem.redeemed_at,
            redeemed_by: lineItem.redeemed_by,
            registrant_name: registration.name,
            registrant_email: registration.email,
            stripe_session_id: registration.stripe_session_id,
            registration_data: registration.registration_data,
            created_at: registration.created_at,
            updated_at: registration.updated_at,
          };
          
          lineItems.push(lineItemData);

          // Track revenue by item type
          const itemAmount = lineItem.line_total_cents || 0;
          switch (lineItem.item_type) {
            case 'tier':
              totalRegistration += itemAmount;
              break;
            case 'spectator_ticket':
              totalSpectatorTickets += itemAmount;
              break;
            case 'extra':
              totalExtras += itemAmount;
              break;
          }
        });
      });

      // Process extras and tickets from pricing_breakdown in registrations
      // This is where the data is actually stored (not in separate tables)
      registrationsData?.forEach(reg => {
        const pricingBreakdown = reg.pricing_breakdown as any || {};
        const registrantData = reg.registrant_data as any || {};
        const paymentStatus = reg.payment_status || reg.status || 'pending';
        const isPaid = paymentStatus === 'paid' || paymentStatus === 'confirmed';
        
        // Use purchaser from registered_by if available
        const purchaser = reg.registered_by ? purchaserProfiles[reg.registered_by] : null;
        const displayName = purchaser?.name || registrantData.coach?.name || registrantData.contact_name || '';
        const displayEmail = purchaser?.email || registrantData.coach?.email || registrantData.contact_email || '';
        const teamName = registrantData.team?.name || registrantData.team_name || '';

        // Base registration revenue + line item
        const registrationCents = Math.round((reg.registration_amount || 0) * 100);
        if (registrationCents > 0) {
          totalRegistration += registrationCents;
          lineItems.push({
            id: `${reg.id}-registration`,
            registration_id: reg.id,
            item_type: 'tier',
            item_name: teamName ? `Registration — ${teamName}` : 'Registration',
            unit_price_cents: registrationCents,
            quantity: 1,
            line_total_cents: registrationCents,
            payment_status: isPaid ? 'paid' : 'pending',
            team_name: teamName,
            registration_date: reg.created_at || '',
            qr_code_value: '',
            is_redeemed: false,
            redeemed_at: undefined,
            redeemed_by: undefined,
            registrant_name: displayName,
            registrant_email: displayEmail,
            stripe_session_id: reg.stripe_session_id || undefined,
            registration_data: registrantData,
            created_at: reg.created_at || '',
            updated_at: reg.updated_at || '',
          });
        }

        // Process extras from pricing_breakdown
        const extras = pricingBreakdown.extras || [];

        extras.forEach((extra: any, idx: number) => {
          const extraPrice = extra.price || 0;
          totalExtras += extraPrice;
          
          lineItems.push({
            id: `${reg.id}-extra-${idx}`,
            registration_id: reg.id,
            item_type: 'extra',
            item_name: extra.name || 'Extra',
            unit_price_cents: extraPrice,
            quantity: 1,
            line_total_cents: extraPrice,
            payment_status: isPaid ? 'paid' : 'pending',
            team_name: teamName,
            registration_date: reg.created_at || '',
            qr_code_value: '',
            is_redeemed: false,
            redeemed_at: undefined,
            redeemed_by: undefined,
            registrant_name: displayName,
            registrant_email: displayEmail,
            stripe_session_id: reg.stripe_session_id || undefined,
            registration_data: registrantData,
            created_at: reg.created_at || '',
            updated_at: reg.updated_at || '',
          });
        });
        
        // Process spectator tickets from pricing_breakdown
        const spectatorTickets = pricingBreakdown.spectator_tickets || [];
        spectatorTickets.forEach((ticket: any, idx: number) => {
          const ticketPrice = ticket.price || 0;
          const quantity = ticket.quantity || 1;
          const lineTotal = ticketPrice * quantity;
          totalSpectatorTickets += lineTotal;
          
          lineItems.push({
            id: `${reg.id}-ticket-${idx}`,
            registration_id: reg.id,
            item_type: 'spectator_ticket',
            item_name: ticket.name || 'Ticket',
            unit_price_cents: ticketPrice,
            quantity: quantity,
            line_total_cents: lineTotal,
            payment_status: isPaid ? 'paid' : 'pending',
            team_name: teamName,
            registration_date: reg.created_at || '',
            qr_code_value: '',
            is_redeemed: false,
            redeemed_at: undefined,
            redeemed_by: undefined,
            registrant_name: displayName,
            registrant_email: displayEmail,
            stripe_session_id: reg.stripe_session_id || undefined,
            registration_data: registrantData,
            created_at: reg.created_at || '',
            updated_at: reg.updated_at || '',
          });
        });
      });

      // Also process from purchased_extras/purchased_tickets tables if they exist (legacy support)
      purchasedExtras?.forEach(extra => {
        const registration = extra.registrations as any;
        const registrantData = registration?.registrant_data || {};
        const paymentStatus = registration?.payment_status || registration?.status || 'pending';
        const isPaid = paymentStatus === 'paid' || paymentStatus === 'confirmed';
        
        const purchaser = registration?.registered_by ? purchaserProfiles[registration.registered_by] : null;
        const displayName = purchaser?.name || registrantData.coach_name || registrantData.name || '';
        const displayEmail = purchaser?.email || registrantData.email || '';
        
        totalExtras += extra.extra_price || 0;
        
        lineItems.push({
          id: extra.id,
          registration_id: extra.registration_id,
          item_type: 'extra',
          item_name: extra.extra_name,
          unit_price_cents: extra.extra_price,
          quantity: 1,
          line_total_cents: extra.extra_price,
          payment_status: isPaid ? 'paid' : 'pending',
          team_name: registrantData.team_name || registrantData.name || '',
          registration_date: extra.created_at || '',
          qr_code_value: extra.qr_code || '',
          is_redeemed: extra.is_redeemed || false,
          redeemed_at: extra.redeemed_at || undefined,
          redeemed_by: extra.redeemed_by || undefined,
          registrant_name: displayName,
          registrant_email: displayEmail,
          stripe_session_id: undefined,
          registration_data: registrantData,
          created_at: extra.created_at || '',
          updated_at: extra.created_at || '',
        });
      });

      purchasedTickets?.forEach(ticket => {
        const registration = ticket.registrations as any;
        const registrantData = registration?.registrant_data || {};
        const paymentStatus = registration?.payment_status || registration?.status || 'pending';
        const isPaid = paymentStatus === 'paid' || paymentStatus === 'confirmed';
        
        const purchaser = registration?.registered_by ? purchaserProfiles[registration.registered_by] : null;
        const displayName = purchaser?.name || registrantData.name || '';
        const displayEmail = purchaser?.email || registrantData.email || '';
        
        totalSpectatorTickets += ticket.ticket_price || 0;
        
        lineItems.push({
          id: ticket.id,
          registration_id: ticket.registration_id,
          item_type: 'spectator_ticket',
          item_name: ticket.ticket_name,
          unit_price_cents: ticket.ticket_price,
          quantity: 1,
          line_total_cents: ticket.ticket_price,
          payment_status: isPaid ? 'paid' : 'pending',
          team_name: registrantData.team_name || registrantData.name || '',
          registration_date: ticket.created_at || '',
          qr_code_value: ticket.qr_code || '',
          is_redeemed: ticket.is_redeemed || false,
          redeemed_at: ticket.redeemed_at || undefined,
          redeemed_by: ticket.redeemed_by || undefined,
          registrant_name: displayName,
          registrant_email: displayEmail,
          stripe_session_id: undefined,
          registration_data: registrantData,
          created_at: ticket.created_at || '',
          updated_at: ticket.created_at || '',
        });
      });

      const totalRevenue = totalRegistration + totalSpectatorTickets + totalExtras;
      const totalRegistrations = registrationsData?.length || 0;
      const paidRegistrations = registrationsData?.filter(r => r.payment_status === 'paid' || r.status === 'paid' || (r.status === 'confirmed' && r.payment_status === 'paid')).length || 0;
      const pendingRegistrations = registrationsData?.filter(r => ['pending', 'venmo_paid'].includes(r.payment_status) || ['pending', 'venmo_paid'].includes(r.status)).length || 0;

      setFinanceData({
        event: eventData,
        revenue: {
          registration: totalRegistration,
          spectator_tickets: totalSpectatorTickets,
          extras: totalExtras,
          total: totalRevenue,
        },
        counts: {
          total_registrations: totalRegistrations,
          paid_registrations: paidRegistrations,
          pending_registrations: pendingRegistrations,
        },
        payment_breakdown: {
          paid: paidAmount,
          pending: pendingAmount,
          comped: compedAmount,
          venmo_paid: venmoPaidAmount,
        },
        line_items: lineItems,
      });

    } catch (error) {
      console.error('Error fetching finance data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLineItemClick = (lineItem: DetailedLineItem) => {
    setSelectedLineItem(lineItem);
    setIsDetailModalOpen(true);
  };

  const handleExportCSV = () => {
    if (!financeData) return;

    // Prepare CSV headers
    const headers = [
      'Registration ID',
      'Team/Name',
      'Email',
      'Item ID',
      'Item Type',
      'Item Name',
      'Quantity',
      'Unit Price',
      'Line Total',
      'Payment Status',
      'Registration Date',
      'Last Updated',
      'Stripe Session ID',
      'QR Code',
      'Is Redeemed',
      'Redeemed At',
      'Redeemed By'
    ];

    // Prepare CSV rows
    const rows = financeData.line_items.map(item => [
      item.registration_id,
      item.registrant_name || '',
      item.registrant_email || '',
      item.id,
      getItemTypeLabel(item.item_type),
      item.item_name,
      item.quantity.toString(),
      formatCurrency(item.unit_price_cents),
      formatCurrency(item.line_total_cents),
      item.payment_status,
      format(new Date(item.created_at), 'yyyy-MM-dd HH:mm:ss'),
      format(new Date(item.updated_at), 'yyyy-MM-dd HH:mm:ss'),
      item.stripe_session_id || '',
      item.qr_code_value || '',
      item.is_redeemed ? 'Yes' : 'No',
      item.redeemed_at ? format(new Date(item.redeemed_at), 'yyyy-MM-dd HH:mm:ss') : '',
      item.redeemed_by || ''
    ]);

    // Convert to CSV format
    const csvContent = [headers, ...rows]
      .map(row => row.map(field => `"${field.toString().replace(/"/g, '""')}"`).join(','))
      .join('\n');

    // Create and download file
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `${financeData.event.name.replace(/[^a-z0-9]/gi, '_').toLowerCase()}_finance_report_${format(new Date(), 'yyyy-MM-dd')}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const formatCurrency = (cents: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(cents / 100);
  };

  const getItemTypeLabel = (type: string) => {
    switch (type) {
      case 'tier':
        return 'Registration';
      case 'spectator_ticket':
        return 'Spectator Ticket';
      case 'extra':
        return 'Event Extra';
      default:
        return type;
    }
  };

  const getPaymentStatusBadge = (status: string) => {
    const statusConfig = {
      paid: { label: 'Paid', variant: 'success' as const, icon: CheckCircle },
      pending: { label: 'Pending', variant: 'outline' as const, icon: Clock },
      comped: { label: 'Comped', variant: 'secondary' as const, icon: AlertCircle },
      venmo_paid: { label: 'Venmo', variant: 'success' as const, icon: CreditCard },
    };

    const config = statusConfig[status as keyof typeof statusConfig] || statusConfig.pending;
    const Icon = config.icon;

    return (
      <Badge variant={config.variant} className="flex items-center gap-1">
        <Icon className="h-3 w-3" />
        {config.label}
      </Badge>
    );
  };

  const filteredLineItems = financeData?.line_items.filter(item => {
    if (paymentFilter === 'all') return true;
    return item.payment_status === paymentFilter;
  }) || [];

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-heading font-bold">Event Finance Report</h1>
        </div>
        <div className="text-center py-8">
          <p className="text-muted-foreground">Loading finance data...</p>
        </div>
      </div>
    );
  }

  const handleEventSelect = (newEventId: string) => {
    setSelectedEventId(newEventId);
    setFinanceData(null); // Clear previous data
  };

  if (!financeData && !selectedEventId && !eventId) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-heading font-bold">Event Finance Report</h1>
        </div>
        
        <Card>
          <CardHeader>
            <CardTitle>Select an Event</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label>Choose an event to view its finance report:</Label>
              <Select onValueChange={handleEventSelect}>
                <SelectTrigger>
                  <SelectValue placeholder="Select an event..." />
                </SelectTrigger>
                <SelectContent>
                  {events.map(event => (
                    <SelectItem key={event.id} value={event.id}>
                      {event.name} - {format(parseEventDate(event.start_date), 'MMM d, yyyy')}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {events.length === 0 && (
              <p className="text-muted-foreground text-sm">No events found. Create an event first to view finance reports.</p>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!financeData) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-heading font-bold">Event Finance Report</h1>
        </div>
        <div className="text-center py-8">
          <p className="text-muted-foreground">No finance data available</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header with Event Selector */}
      <div className="flex items-center justify-between">
        <div className="flex-1">
          <h1 className="text-2xl font-heading font-bold">Finance Report</h1>
          {financeData ? (
            <>
              <p className="text-muted-foreground">{financeData.event.name}</p>
              {financeData.event.start_date && (
                <p className="text-sm text-muted-foreground">
                  {format(parseEventDate(financeData.event.start_date), 'MMM d, yyyy')}
                  {financeData.event.end_date && financeData.event.end_date !== financeData.event.start_date && (
                    <span> - {format(parseEventDate(financeData.event.end_date), 'MMM d, yyyy')}</span>
                  )}
                </p>
              )}
            </>
          ) : (
            <p className="text-muted-foreground">Select an event to view its finance report</p>
          )}
        </div>
        
        {!eventId && (
          <div className="flex items-center space-x-4">
            <div className="min-w-[300px]">
              <Select value={selectedEventId} onValueChange={handleEventSelect}>
                <SelectTrigger>
                  <SelectValue placeholder="Select an event..." />
                </SelectTrigger>
                <SelectContent>
                  {events.map(event => (
                    <SelectItem key={event.id} value={event.id}>
                      {event.name} - {format(parseEventDate(event.start_date), 'MMM d, yyyy')}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        )}
        
        {financeData && (
          <Button className="flex items-center gap-2" onClick={handleExportCSV}>
            <Download className="h-4 w-4" />
            Export Report
          </Button>
        )}
      </div>

      {!financeData && selectedEventId && (
        <div className="text-center py-8">
          <p className="text-muted-foreground">Loading finance data...</p>
        </div>
      )}

      {financeData && (
        <>
          {/* Revenue Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Registration Revenue</CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{formatCurrency(financeData.revenue.registration)}</div>
            <p className="text-xs text-muted-foreground">From team registrations</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Spectator Tickets</CardTitle>
            <FileText className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{formatCurrency(financeData.revenue.spectator_tickets)}</div>
            <p className="text-xs text-muted-foreground">From ticket sales</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Event Extras</CardTitle>
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{formatCurrency(financeData.revenue.extras)}</div>
            <p className="text-xs text-muted-foreground">From merchandise & extras</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Revenue</CardTitle>
            <DollarSign className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-primary">{formatCurrency(financeData.revenue.total)}</div>
            <p className="text-xs text-muted-foreground">All categories combined</p>
          </CardContent>
        </Card>
      </div>

      {/* Registration Stats */}
      <Card>
        <CardHeader>
          <CardTitle>Registration Overview</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="text-center">
              <div className="text-3xl font-bold">{financeData.counts.total_registrations}</div>
              <p className="text-sm text-muted-foreground">Total Registrations</p>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-green-600">{financeData.counts.paid_registrations}</div>
              <p className="text-sm text-muted-foreground">Paid Registrations</p>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-orange-600">{financeData.counts.pending_registrations}</div>
              <p className="text-sm text-muted-foreground">Pending Payments</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Payment Status Breakdown */}
      <Card>
        <CardHeader>
          <CardTitle>Payment Status Breakdown</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg">
              <div>
                <p className="text-sm font-medium">Paid</p>
                <p className="text-2xl font-bold text-green-600">{formatCurrency(financeData.payment_breakdown.paid)}</p>
              </div>
              <CheckCircle className="h-8 w-8 text-green-600" />
            </div>
            <div className="flex items-center justify-between p-3 bg-orange-50 rounded-lg">
              <div>
                <p className="text-sm font-medium">Pending</p>
                <p className="text-2xl font-bold text-orange-600">{formatCurrency(financeData.payment_breakdown.pending)}</p>
              </div>
              <Clock className="h-8 w-8 text-orange-600" />
            </div>
            <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg">
              <div>
                <p className="text-sm font-medium">Venmo</p>
                <p className="text-2xl font-bold text-blue-600">{formatCurrency(financeData.payment_breakdown.venmo_paid)}</p>
              </div>
              <CreditCard className="h-8 w-8 text-blue-600" />
            </div>
            <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
              <div>
                <p className="text-sm font-medium">Comped</p>
                <p className="text-2xl font-bold text-gray-600">{formatCurrency(financeData.payment_breakdown.comped)}</p>
              </div>
              <AlertCircle className="h-8 w-8 text-gray-600" />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Detailed Line Items */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>Detailed Line Items (Click for Details)</CardTitle>
            <Select value={paymentFilter} onValueChange={setPaymentFilter}>
              <SelectTrigger className="w-48">
                <SelectValue placeholder="Filter by payment status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Payments</SelectItem>
                <SelectItem value="paid">Paid Only</SelectItem>
                <SelectItem value="pending">Pending Only</SelectItem>
                <SelectItem value="venmo_paid">Venmo Only</SelectItem>
                <SelectItem value="comped">Comped Only</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Team/Item</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Item Name</TableHead>
                <TableHead>Quantity</TableHead>
                <TableHead>Unit Price</TableHead>
                <TableHead>Total</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Date</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredLineItems.map((item) => (
                <TableRow 
                  key={item.id} 
                  className="cursor-pointer hover:bg-muted/50 transition-colors"
                  onClick={() => handleLineItemClick(item)}
                >
                  <TableCell className="font-medium">{item.team_name || 'N/A'}</TableCell>
                  <TableCell>{getItemTypeLabel(item.item_type)}</TableCell>
                  <TableCell>{item.item_name}</TableCell>
                  <TableCell>{item.quantity}</TableCell>
                  <TableCell>{formatCurrency(item.unit_price_cents)}</TableCell>
                  <TableCell>{formatCurrency(item.line_total_cents)}</TableCell>
                  <TableCell>{getPaymentStatusBadge(item.payment_status)}</TableCell>
                  <TableCell>{format(new Date(item.registration_date), 'MMM d, yyyy')}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          
          {filteredLineItems.length === 0 && (
            <div className="text-center py-8">
              <p className="text-muted-foreground">No line items found for the selected filter</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Detail Modal */}
      <Dialog open={isDetailModalOpen} onOpenChange={setIsDetailModalOpen}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Eye className="h-5 w-5" />
              Line Item Details
            </DialogTitle>
          </DialogHeader>
          
          {selectedLineItem && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Registration Information */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <User className="h-4 w-4" />
                    Registration Information
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-sm text-muted-foreground">Registration ID:</span>
                    <span className="text-sm font-mono">{selectedLineItem.registration_id}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-muted-foreground">Team/Name:</span>
                    <span className="text-sm font-medium">{selectedLineItem.registrant_name}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-muted-foreground">Email:</span> 
                    <span className="text-sm">{selectedLineItem.registrant_email}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-muted-foreground">Registration Date:</span>
                    <span className="text-sm">{format(new Date(selectedLineItem.created_at), 'MMM d, yyyy h:mm a')}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-muted-foreground">Last Updated:</span>
                    <span className="text-sm">{format(new Date(selectedLineItem.updated_at), 'MMM d, yyyy h:mm a')}</span>
                  </div>
                </CardContent>
              </Card>

              {/* Payment Information */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <CreditCard className="h-4 w-4" />
                    Payment Information
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-sm text-muted-foreground">Payment Status:</span>
                    {getPaymentStatusBadge(selectedLineItem.payment_status)}
                  </div>
                  {selectedLineItem.stripe_session_id && (
                    <div className="flex justify-between">
                      <span className="text-sm text-muted-foreground">Stripe Session ID:</span>
                      <span className="text-sm font-mono text-xs break-all">{selectedLineItem.stripe_session_id}</span>
                    </div>
                  )}
                  <div className="flex justify-between">
                    <span className="text-sm text-muted-foreground">Unit Price:</span>
                    <span className="text-sm font-medium">{formatCurrency(selectedLineItem.unit_price_cents)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-muted-foreground">Quantity:</span>
                    <span className="text-sm">{selectedLineItem.quantity}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-muted-foreground">Line Total:</span>
                    <span className="text-sm font-bold">{formatCurrency(selectedLineItem.line_total_cents)}</span>
                  </div>
                </CardContent>
              </Card>

              {/* Item Information */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Hash className="h-4 w-4" />
                    Item Information
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-sm text-muted-foreground">Item ID:</span>
                    <span className="text-sm font-mono">{selectedLineItem.id}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-muted-foreground">Item Type:</span>
                    <span className="text-sm">{getItemTypeLabel(selectedLineItem.item_type)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-muted-foreground">Item Name:</span>
                    <span className="text-sm font-medium">{selectedLineItem.item_name}</span>
                  </div>
                  {selectedLineItem.qr_code_value && (
                    <div className="flex justify-between">
                      <span className="text-sm text-muted-foreground">QR Code:</span>
                      <span className="text-sm font-mono text-xs break-all">{selectedLineItem.qr_code_value}</span>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Redemption Status */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    {selectedLineItem.is_redeemed ? (
                      <CheckCircle className="h-4 w-4 text-green-600" />
                    ) : (
                      <XCircle className="h-4 w-4 text-red-600" />
                    )}
                    Redemption Status
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-muted-foreground">Status:</span>
                    <Badge variant={selectedLineItem.is_redeemed ? 'success' : 'outline'}>
                      {selectedLineItem.is_redeemed ? 'Redeemed' : 'Not Redeemed'}
                    </Badge>
                  </div>
                  {selectedLineItem.is_redeemed && selectedLineItem.redeemed_at && (
                    <div className="flex justify-between">
                      <span className="text-sm text-muted-foreground">Redeemed At:</span>
                      <span className="text-sm">{format(new Date(selectedLineItem.redeemed_at), 'MMM d, yyyy h:mm a')}</span>
                    </div>
                  )}
                  {selectedLineItem.redeemed_by && (
                    <div className="flex justify-between">
                      <span className="text-sm text-muted-foreground">Redeemed By:</span>
                      <span className="text-sm font-mono">{selectedLineItem.redeemed_by}</span>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Additional Registration Data */}
              {selectedLineItem.registration_data && (
                <Card className="md:col-span-2">
                  <CardHeader>
                    <CardTitle className="text-lg">Additional Registration Data</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <pre className="text-xs bg-muted p-3 rounded-md overflow-x-auto">
                      {JSON.stringify(selectedLineItem.registration_data, null, 2)}
                    </pre>
                  </CardContent>
                </Card>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
        </>
      )}
    </div>
  );
};

export default EventFinanceReports;